import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { X, Search, Users, Loader2, CheckSquare, Square, RefreshCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

const SEARCH_DEBOUNCE_MS = 250;

interface SchuelerRow {
  ID: number;
  sophomorixUnid?: string;
  Vorname?: string;
  Name?: string;
  Klasse?: string;
  Email?: string;
}

interface KlassenRow {
  Klasse: string;
  student_count?: number;
}

export interface PaymentDemandStudentPickerModalProps {
  open: boolean;
  campaignId: number;
  campaignTitle: string;
  defaultDueAt?: string | null;
  /** Standardbetrag aus Vorgang (Metadaten), z. B. nach Bearbeiten */
  defaultAmountEuro?: number | null;
  onClose: () => void;
  onCreated: () => void;
}

export default function PaymentDemandStudentPickerModal({
  open,
  campaignId,
  campaignTitle,
  defaultDueAt,
  defaultAmountEuro,
  onClose,
  onCreated
}: PaymentDemandStudentPickerModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedKlasse, setSelectedKlasse] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [amount, setAmount] = useState<string>('25');
  const [dueAt, setDueAt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (open) {
      const d = defaultDueAt ? String(defaultDueAt).slice(0, 10) : '';
      setDueAt(d);
      setSearchTerm('');
      setDebouncedSearch('');
      setSelectedKlasse('');
      setSelectedIds([]);
      const n =
        defaultAmountEuro != null && Number.isFinite(Number(defaultAmountEuro)) && Number(defaultAmountEuro) > 0
          ? Number(defaultAmountEuro)
          : null;
      setAmount(n != null ? String(n).replace('.', ',') : '25');
    }
  }, [open, defaultDueAt, defaultAmountEuro]);

  const classesQuery = useQuery(
    ['students-classes-picker'],
    async () => {
      const res = await api.get('/students/classes');
      return (res.data?.data ?? []) as KlassenRow[];
    },
    { enabled: open }
  );

  const studentsQuery = useQuery(
    ['students-picker', debouncedSearch, selectedKlasse],
    async () => {
      const res = await api.get('/students', {
        params: {
          search: debouncedSearch || undefined,
          klasse: selectedKlasse || undefined,
          includePhotos: 'false',
          sortField: 'Name',
          sortDirection: 'asc'
        }
      });
      return (res.data?.data ?? []) as SchuelerRow[];
    },
    { enabled: open, keepPreviousData: true }
  );

  const students = useMemo(() => studentsQuery.data ?? [], [studentsQuery.data]);

  useEffect(() => {
    if (!students.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds((prev) => prev.filter((id) => students.some((s) => String(s.ID) === id)));
  }, [students]);

  const toggleSelection = useCallback((studentId: string) => {
    setSelectedIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (!students.length) return;
    const visible = students.map((s) => String(s.ID));
    const all = visible.every((id) => selectedIds.includes(id));
    setSelectedIds(all ? [] : visible);
  }, [students, selectedIds]);

  const avatarUrl = (s: SchuelerRow) => {
    const label = `${s.Vorname ?? ''} ${s.Name ?? ''}`.trim() || 'Schüler';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(label)}&background=3B82F6&color=ffffff&size=64`;
  };

  const handleCreate = async () => {
    const amt = parseFloat(amount.replace(',', '.'));
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Bitte einen gültigen Betrag eingeben.');
      return;
    }
    if (!selectedIds.length) {
      toast.error('Bitte mindestens einen Schüler auswählen.');
      return;
    }
    const byId = new Map(students.map((s) => [String(s.ID), s]));
    const payloadStudents = selectedIds
      .map((id) => {
        const s = byId.get(id);
        if (!s) return null;
        const vor = String(s.Vorname ?? '').trim();
        const name = String(s.Name ?? '').trim();
        const full = `${vor} ${name}`.trim() || name || String(s.ID);
        return {
          id: String(s.ID),
          name: full,
          email: s.Email && String(s.Email).trim() ? String(s.Email).trim() : null
        };
      })
      .filter(Boolean) as Array<{ id: string; name: string; email: string | null }>;

    if (!payloadStudents.length) {
      toast.error('Ausgewählte Schüler nicht in der aktuellen Liste.');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await api.post(`/payments/v2/campaigns/${campaignId}/demands`, {
        common: {
          amount: amt,
          currency: 'EUR',
          due_at: dueAt || null
        },
        students: payloadStudents
      });
      if (resp.data?.success) {
        toast.success(`Forderungen erstellt: ${resp.data?.data?.created_count ?? payloadStudents.length}`);
        onCreated();
        onClose();
      } else {
        toast.error(resp.data?.message || 'Forderungen konnten nicht erstellt werden');
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Forderungen konnten nicht erstellt werden';
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const loading = studentsQuery.isLoading || studentsQuery.isFetching;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex h-full max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-6 py-5">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Weitere Forderungen anlegen</h2>
            <p className="text-sm text-gray-500">Vorgang: {campaignTitle}</p>
            <p className="text-sm text-gray-500">
              Zusätzliche Schüler auswählen, Betrag und optional Fälligkeit prüfen — bestehende Forderungen bleiben
              unverändert.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b bg-gray-50 px-6 py-4">
          <div className="mb-4 grid gap-3 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Betrag je Forderung (EUR)</label>
              <input
                type="number"
                min={0.01}
                step={0.01}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Fälligkeit (optional)</label>
              <input
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div className="flex items-end text-xs text-gray-500">
              {selectedIds.length} Schüler ausgewählt
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[2fr_1fr_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Schüler suchen (Vorname, Name, Klasse …)"
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                type="search"
              />
            </div>

            <div>
              <select
                value={selectedKlasse}
                onChange={(e) => setSelectedKlasse(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="">Alle Klassen</option>
                {(classesQuery.data ?? []).map((row) => (
                  <option key={row.Klasse} value={row.Klasse}>
                    Klasse {row.Klasse}
                    {row.student_count != null ? ` (${row.student_count})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleSelectAll}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100"
              >
                {students.length > 0 && students.every((s) => selectedIds.includes(String(s.ID))) ? (
                  <>
                    <CheckSquare className="mr-2 h-3.5 w-3.5" />
                    Alle abwählen
                  </>
                ) : (
                  <>
                    <Square className="mr-2 h-3.5 w-3.5" />
                    Alle auswählen
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => studentsQuery.refetch()}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100"
              >
                <RefreshCcw className="mr-2 h-3.5 w-3.5" />
                Aktualisieren
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Lade Schülerdaten…</span>
            </div>
          ) : students.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-gray-500">
              <Users className="h-10 w-10" />
              <span>Keine Schüler gefunden</span>
            </div>
          ) : (
            <div className="h-full overflow-y-auto px-6 py-4">
              <ul className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {students.slice(0, 800).map((s) => {
                  const sid = String(s.ID);
                  const selected = selectedIds.includes(sid);
                  return (
                    <li
                      key={sid}
                      className={`flex gap-3 rounded-xl border p-3 transition-shadow ${
                        selected ? 'border-primary-500 bg-primary-50 shadow-sm' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSelection(sid)}
                        className="flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-full border border-gray-300 bg-white"
                        aria-pressed={selected}
                      >
                        {selected ? (
                          <CheckSquare className="h-5 w-5 text-primary-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>

                      <img
                        src={avatarUrl(s)}
                        alt=""
                        className="h-12 w-12 shrink-0 self-start rounded-full object-cover"
                      />

                      <div className="min-w-0 flex-1 overflow-hidden">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {s.Vorname} {s.Name}
                        </p>
                        <p className="truncate text-xs text-gray-500">Klasse {s.Klasse || '—'}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {students.length > 800 && (
                <p className="mt-3 text-center text-xs text-amber-700">
                  Es werden nur die ersten 800 Treffer angezeigt — Suche oder Klasse einschränken.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="border-t bg-gray-50 px-6 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-gray-600">
              <span>{selectedIds.length} ausgewählt</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                disabled={!selectedIds.length}
              >
                Auswahl leeren
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={submitting || !selectedIds.length}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Erstelle…' : 'Forderungen erstellen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
